/* AdminSupportChatsPanel — admin view of every AI support chat session
 * (the marketing/lead-capture channel — separate from the authenticated
 * /support ticket system).
 *
 * Capabilities:
 *  - Paginated list, newest first, search by email/text.
 *  - Click a row → see the full transcript inline.
 *  - Delete a chat (GDPR / cleanup).
 *  - Export all opted-in emails to CSV for marketing.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  MessageCircle, Search, Loader2, Download, Trash2, ChevronLeft, RefreshCw, Mail,
} from 'lucide-react';
import { toast } from 'sonner';

const PAGE_SIZE = 25;

const formatDt = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso || '';
  }
};

export const AdminSupportChatsPanel = ({ api }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(null); // detail view: full chat doc
  const [activeLoading, setActiveLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/support-chats', {
        params: { skip, limit: PAGE_SIZE, q: q || undefined },
      });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load support chats');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, skip, q]);

  useEffect(() => { load(); }, [load]);

  const openThread = async (chatId) => {
    setActiveLoading(true);
    setActive({ id: chatId, loading: true });
    try {
      const r = await api.get(`/admin/support-chats/${chatId}`);
      setActive(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load chat');
      setActive(null);
    } finally {
      setActiveLoading(false);
    }
  };

  const handleDelete = async (chat) => {
    if (!window.confirm(`Delete chat from ${chat.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/support-chats/${chat.id}`);
      toast.success('Chat deleted');
      setActive(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleExport = async () => {
    try {
      const r = await api.get('/admin/support-chats-export/emails.csv', {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `support-chat-emails-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Email list downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    }
  };

  // ─── Detail view ───
  if (active) {
    return (
      <Card className="border-zinc-200" data-testid="support-chat-detail">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActive(null)}
              data-testid="support-chat-back"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to list
            </Button>
            {!activeLoading && active.id && !active.loading && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDelete(active)}
                className="text-rose-700 border-rose-200 hover:bg-rose-50"
                data-testid="support-chat-delete"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
            )}
          </div>
          {!activeLoading && active.email && (
            <>
              <CardTitle className="text-base flex items-center gap-2 mt-2">
                <Mail className="w-4 h-4 text-indigo-600" />
                {active.email}
              </CardTitle>
              <CardDescription className="text-xs">
                Started {formatDt(active.created_at)} · last activity{' '}
                {formatDt(active.updated_at)}{' '}
                {active.first_page && (
                  <> · from <span className="font-mono">{active.first_page}</span></>
                )}
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent>
          {activeLoading || active.loading ? (
            <div className="flex items-center justify-center py-8 text-zinc-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Loading transcript…
            </div>
          ) : (
            <ScrollArea className="h-[460px] pr-3">
              <div className="space-y-3">
                {(active.messages || []).map((m, i) => (
                  <div
                    key={i}
                    className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
                    data-testid={`admin-chat-msg-${m.role}-${i}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                        m.role === 'user'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-zinc-100 text-zinc-800'
                      }`}
                    >
                      {m.text}
                      <div
                        className={`mt-1 text-[10px] ${
                          m.role === 'user' ? 'text-indigo-100/80' : 'text-zinc-500'
                        }`}
                      >
                        {m.role} · {formatDt(m.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    );
  }

  // ─── List view ───
  return (
    <Card className="border-zinc-200" data-testid="admin-support-chats-panel">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageCircle className="w-4 h-4 text-indigo-600" />
              AI Support Chats
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              Every conversation captured by the floating chat widget on public pages.
              Every visitor who used the widget consented to receive product updates.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="support-chats-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={handleExport}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="support-chats-export"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Export emails (CSV)
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative mb-3">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => { setSkip(0); setQ(e.target.value); }}
            placeholder="Search email or message text…"
            className="pl-9 h-9 text-sm"
            data-testid="support-chats-search"
          />
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-zinc-500 text-sm">
            No support chats yet. They'll show up here as visitors use the widget.
          </div>
        ) : (
          <>
            <div className="border border-zinc-200 rounded-lg divide-y divide-zinc-100 overflow-hidden">
              {items.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => openThread(c.id)}
                  className="w-full text-left px-3 py-3 hover:bg-zinc-50 transition-colors"
                  data-testid={`support-chat-row-${c.id}`}
                >
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <span className="text-sm font-medium text-zinc-900 truncate">
                      {c.email}
                    </span>
                    <Badge variant="outline" className="text-[10px] flex-shrink-0">
                      {c.message_count} msg{c.message_count !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  {c.last_user_message && (
                    <div className="text-xs text-zinc-600 truncate">
                      “{c.last_user_message}”
                    </div>
                  )}
                  <div className="text-[10px] text-zinc-400 mt-0.5">
                    {formatDt(c.updated_at)}{c.first_page ? ` · ${c.first_page}` : ''}
                  </div>
                </button>
              ))}
            </div>
            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between mt-3 text-xs text-zinc-500">
                <span>
                  Showing {skip + 1}–{Math.min(skip + items.length, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={skip === 0}
                    onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                  >
                    Prev
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={skip + PAGE_SIZE >= total}
                    onClick={() => setSkip(skip + PAGE_SIZE)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
